/**
 * SpotlightCard Component
 *
 * Larger, more prominent card for the "For You" spotlight section.
 * Compared to regular TrainingCard:
 * - Larger icon area (48x48 vs 44x44)
 * - WhyBadge displayed prominently
 * - Subtle gradient background tint (green for in-progress, blue for not started)
 * - In-progress items show a progress bar and "Continue" CTA
 * - Full card is tappable via OperatorCard href
 */

import { useNavigate } from 'react-router-dom';
import { Video, FileText, PlayCircle, ArrowRight, Clock, AlertTriangle } from 'lucide-react';
import type { TrainingItem } from '../../../hooks/useOperatorPortal';
import { OperatorCard } from './OperatorCard';
import { WhyBadge } from './WhyBadge';
import { formatDuration, formatDueDate } from '../utils';

interface SpotlightCardProps {
  item: TrainingItem;
  index: number;
}

export function SpotlightCard({ item, index }: SpotlightCardProps) {
  const navigate = useNavigate();
  const isVideo = item.material.materialType === 'video';
  const isInProgress = item.status === 'in_progress';
  const isOverdue = item.isOverdue && item.status !== 'completed';

  // Background tint based on state
  const bgTint = isInProgress
    ? 'bg-gradient-to-br from-emerald-50/60 to-white'
    : isOverdue
    ? 'bg-gradient-to-br from-red-50/60 to-white'
    : 'bg-gradient-to-br from-blue-50/60 to-white';

  return (
    <div
      style={{
        animation: `fadeInUp 0.4s ease-out ${index < 5 ? index * 0.06 : 0}s both`,
      }}
    >
      <OperatorCard
        href={`/operator/content/${item.assignmentId}`}
        className={`overflow-hidden !p-0 ${bgTint}`}
      >
        {/* Progress bar at top for in-progress items */}
        {isInProgress && (
          <div className="h-1.5 w-full bg-emerald-100">
            <div
              className="h-full rounded-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${item.progress}%` }}
            />
          </div>
        )}

        <div className="p-4">
          <div className="flex items-start gap-3.5">
            {/* Material Type Icon — larger than TrainingCard */}
            <div
              className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${
                isInProgress
                  ? 'bg-emerald-100 text-emerald-600'
                  : isVideo
                  ? 'bg-violet-100 text-violet-600'
                  : 'bg-amber-100 text-amber-600'
              }`}
            >
              {isInProgress ? (
                <PlayCircle size={24} strokeWidth={1.5} />
              ) : isVideo ? (
                <Video size={24} strokeWidth={1.5} />
              ) : (
                <FileText size={24} strokeWidth={1.5} />
              )}
            </div>

            {/* Content */}
            <div className="min-w-0 flex-1">
              {/* CTA label for in-progress */}
              {isInProgress && (
                <p className="mb-0.5 text-xs font-semibold text-emerald-600">
                  Continue where you left off
                </p>
              )}

              {/* Overdue label */}
              {isOverdue && !isInProgress && (
                <p className="mb-0.5 flex items-center gap-1 text-xs font-semibold text-red-600">
                  <AlertTriangle size={12} strokeWidth={1.5} />
                  Overdue
                </p>
              )}

              <h3 className="text-base font-semibold text-op-text-primary line-clamp-2">
                {item.material.title}
              </h3>

              {item.material.description && (
                <p className="mt-1 text-sm text-op-text-secondary line-clamp-2">
                  {item.material.description}
                </p>
              )}

              {/* WhyBadge — shown prominently */}
              {(item.gapDescription || item.generatedBy) && (
                <div className="mt-2">
                  <WhyBadge
                    generatedBy={item.generatedBy}
                    sopName={item.sopName}
                    gapDescription={item.gapDescription}
                    currentScoreLabel={item.currentScoreLabel}
                    targetScoreLabel={item.targetScoreLabel}
                    variant="compact"
                  />
                </div>
              )}

              {/* Meta row */}
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-op-text-tertiary">
                {/* Type badge */}
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    isVideo
                      ? 'bg-violet-50 text-violet-600'
                      : 'bg-amber-50 text-amber-600'
                  }`}
                >
                  {isVideo ? 'Video' : 'Document'}
                </span>

                {/* Duration */}
                {item.material.duration && isVideo && (
                  <span className="flex items-center gap-1">
                    <Clock size={13} strokeWidth={1.5} />
                    {formatDuration(item.material.duration)}
                  </span>
                )}

                {/* Due date */}
                {item.dueDate && (
                  <span
                    className={`flex items-center gap-1 ${
                      isOverdue ? 'font-medium text-red-600' : ''
                    }`}
                  >
                    {isOverdue && <AlertTriangle size={13} strokeWidth={1.5} />}
                    {formatDueDate(item.dueDate)}
                  </span>
                )}

                {/* Progress */}
                {isInProgress && item.progress > 0 && (
                  <span className="font-medium text-emerald-600">{item.progress}%</span>
                )}

                {/* View SOP link */}
                {item.sopId && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      navigate(`/operator/sops/${item.sopId}`);
                    }}
                    className="flex items-center gap-1 font-medium text-guild-green"
                  >
                    <FileText size={13} strokeWidth={1.5} />
                    View SOP
                  </button>
                )}
              </div>
            </div>

            {/* Arrow */}
            <ArrowRight size={18} strokeWidth={1.5} className="mt-1 flex-shrink-0 text-op-text-tertiary" />
          </div>
        </div>
      </OperatorCard>
    </div>
  );
}
